import type { ProviderBlackoutWindow, ProviderVenue, VenueOperatingHoursEntry } from "../../lib/api";
import type { TranslationFunction } from "../../lib/preferences";

const dayLabels = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

const weekdayLookup: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function getDayLabel(dayOfWeek: number) {
  return dayLabels[dayOfWeek] ?? `Day ${dayOfWeek}`;
}

export function getVenueOpenState(
  operatingHours: VenueOperatingHoursEntry[],
  timezone: string,
  t?: TranslationFunction,
  now: Date = new Date(),
) {
  try {
    const localDateTime = new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hourCycle: "h23",
      timeZone: timezone,
    }).formatToParts(now);
    const weekday = localDateTime.find((part) => part.type === "weekday")?.value;
    const hour = Number(localDateTime.find((part) => part.type === "hour")?.value);
    const minute = Number(localDateTime.find((part) => part.type === "minute")?.value);
    const second = Number(localDateTime.find((part) => part.type === "second")?.value);
    const dayOfWeek = weekday ? weekdayLookup[weekday] : undefined;
    if (dayOfWeek === undefined || !Number.isFinite(hour) || !Number.isFinite(minute) || !Number.isFinite(second)) {
      throw new Error("Unable to resolve local time");
    }
    const currentSeconds = hour * 3600 + minute * 60 + second;
    const currentDay = operatingHours.find((entry) => entry.day_of_week === dayOfWeek);
    const previousDay = operatingHours.find(
      (entry) => entry.day_of_week === (dayOfWeek + 6) % 7,
    );
    const parseMinutes = (value: string | null) => {
      if (!value || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return null;
      const [hoursText, minutesText] = value.split(":");
      return Number(hoursText) * 60 + Number(minutesText);
    };

    const currentOpenMinutes = currentDay?.is_open ? parseMinutes(currentDay.open_time) : null;
    const currentCloseMinutes = currentDay?.is_open ? parseMinutes(currentDay.close_time) : null;
    const previousOpenMinutes = previousDay?.is_open ? parseMinutes(previousDay.open_time) : null;
    const previousCloseMinutes = previousDay?.is_open ? parseMinutes(previousDay.close_time) : null;

    // An overnight window belongs to the day on which it opened. Check it
    // first so Tuesday 00:15 can still be covered by Monday's 23:00–00:30.
    const isInPreviousOvernightWindow =
      previousOpenMinutes !== null &&
      previousCloseMinutes !== null &&
      previousCloseMinutes < previousOpenMinutes &&
      currentSeconds <= previousCloseMinutes * 60;

    if (isInPreviousOvernightWindow) {
      return {
        isOpenNow: true,
        reason: t ? t("Open until {time}", { time: previousDay?.close_time }) : `Open until ${previousDay?.close_time}`,
      };
    }

    if (!currentDay || !currentDay.is_open || currentOpenMinutes === null || currentCloseMinutes === null) {
      return {
        isOpenNow: false,
        reason: t ? t("{day} is closed", { day: t(getDayLabel(dayOfWeek)) }) : `${getDayLabel(dayOfWeek)} is closed`,
      };
    }

    const isOvernight = currentCloseMinutes < currentOpenMinutes;
    const isOpenNow = isOvernight
      ? currentSeconds >= currentOpenMinutes * 60
      : currentSeconds >= currentOpenMinutes * 60 && currentSeconds <= currentCloseMinutes * 60;

    return {
      isOpenNow,
      reason: isOpenNow
        ? t ? t(isOvernight ? "Open until {time} tomorrow" : "Open until {time}", { time: currentDay.close_time }) : `Open until ${currentDay.close_time}${isOvernight ? " tomorrow" : ""}`
        : t ? t("Closed now · opens {time}", { time: currentDay.open_time }) : `Closed now · opens ${currentDay.open_time}`,
    };
  } catch {
    return {
      isOpenNow: false,
      reason: t ? t("Timezone unavailable") : "Timezone unavailable",
    };
  }
}

/** Note stored on closures created by "Close early today", so they can be undone with "Reopen". */
export const CLOSE_EARLY_NOTE = "Closed early today";

function localParts(now: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: timezone,
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${get("hour")}:${get("minute")}` };
}

function addDays(isoDate: string, days: number) {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatClosureEnd(endIso: string, timezone: string, now: Date) {
  const end = new Date(endIso);
  const sameDay = localParts(end, timezone).date === localParts(now, timezone).date;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    ...(sameDay ? {} : { weekday: "short", day: "numeric", month: "short" }),
  }).format(end);
}

export interface VenueStatus {
  isOpenNow: boolean;
  reason: string;
  /** Where the status comes from: the weekly opening hours, or a closure (blackout) covering now. */
  source: "hours" | "closure";
  activeClosure: ProviderBlackoutWindow | null;
  /** True when the active closure was made with "Close early today" and can be undone. */
  closedEarly: boolean;
  /** Local start/end for a "Close early today" closure, or null when the venue isn't open now. */
  closeEarlyWindow: { start_local: string; end_local: string } | null;
}

/**
 * The venue's open/closed state as players see it: a closure covering now wins over
 * the weekly hours. The booking API also refuses bookings during closures.
 */
export function getVenueStatus(
  venue: Pick<ProviderVenue, "operating_hours" | "timezone" | "blackout_windows">,
  t?: TranslationFunction,
  now: Date = new Date(),
): VenueStatus {
  const activeClosure = (venue.blackout_windows ?? []).find(
    (window) => new Date(window.start_at) <= now && now < new Date(window.end_at),
  ) ?? null;
  if (activeClosure) {
    const until = formatClosureEnd(activeClosure.end_at, venue.timezone, now);
    return {
      isOpenNow: false,
      reason: t ? t("Closed until {time}", { time: until }) : `Closed until ${until}`,
      source: "closure",
      activeClosure,
      closedEarly: activeClosure.note === CLOSE_EARLY_NOTE,
      closeEarlyWindow: null,
    };
  }

  const hours = getVenueOpenState(venue.operating_hours, venue.timezone, t, now);
  let closeEarlyWindow: VenueStatus["closeEarlyWindow"] = null;
  if (hours.isOpenNow) {
    const local = localParts(now, venue.timezone);
    const dayOfWeek = new Date(`${local.date}T00:00:00Z`).getUTCDay();
    const today = venue.operating_hours.find((entry) => entry.day_of_week === dayOfWeek);
    const yesterday = venue.operating_hours.find((entry) => entry.day_of_week === (dayOfWeek + 6) % 7);
    const nowMinutes = Number(local.time.slice(0, 2)) * 60 + Number(local.time.slice(3));
    const toMinutes = (value: string | null) => (value ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5)) : null);
    let endLocal: string | null = null;
    const yOpen = yesterday?.is_open ? toMinutes(yesterday.open_time) : null;
    const yClose = yesterday?.is_open ? toMinutes(yesterday.close_time) : null;
    if (yOpen !== null && yClose !== null && yClose < yOpen && nowMinutes <= yClose) {
      endLocal = `${local.date}T${yesterday!.close_time!.slice(0, 5)}`;
    } else if (today?.is_open && today.open_time && today.close_time) {
      const overnight = (toMinutes(today.close_time) ?? 0) < (toMinutes(today.open_time) ?? 0);
      endLocal = `${overnight ? addDays(local.date, 1) : local.date}T${today.close_time.slice(0, 5)}`;
    }
    const startLocal = `${local.date}T${local.time}`;
    if (endLocal && endLocal > startLocal) closeEarlyWindow = { start_local: startLocal, end_local: endLocal };
  }
  return { ...hours, source: "hours", activeClosure: null, closedEarly: false, closeEarlyWindow };
}
